import { useState, useEffect } from 'react';
import { Star } from 'lucide-react';

import { apiService } from '../services/api';
import { getStaticData } from '../hooks/useStaticData';

import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

const ReviewsSection = () => {
  // Use static data as initial state to avoid flash of default content
  const [testimonials, setTestimonials] = useState(() => getStaticData('testimonials') || []);
  const [loading, setLoading] = useState(() => {
    // Only show loading if we don't have static data
    const staticTestimonials = getStaticData('testimonials');
    return !staticTestimonials || staticTestimonials.length === 0;
  });

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const testimonialsData = await apiService.getTestimonials();
        if (testimonialsData) setTestimonials(testimonialsData);
      } catch (error) {
        console.error('Failed to load testimonials:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star 
        key={index} 
        className={`h-5 w-5 ${index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));
  };

  if (loading) {
    return (
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-300 rounded w-80 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-300 rounded w-96 mx-auto"></div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-300 h-48 rounded-lg"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="reviews" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            What Our Students Say
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real stories from real students who have transformed their lives through 
            martial arts training at Owaza.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial) => (
            <Card key={testimonial.id} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg h-full flex flex-col">
              <CardContent className="p-6 flex flex-col flex-1">
                <div className="flex flex-col flex-1">
                  {/* Stars */}
                  <div className="flex items-center space-x-1">
                    {renderStars(testimonial.rating)}
                  </div>

                  {/* Review Text */}
                  <blockquote className="text-gray-700 italic leading-relaxed mt-4 flex-1">
                    "{testimonial.text}"
                  </blockquote>

                  {/* Author Info */}
                  <div className="flex items-center justify-between pt-4 border-t mt-4">
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {testimonial.program}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Google Reviews CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">Want to see more reviews?</p>
          <a 
            href="https://www.google.com/search?q=owaza+martial+arts+spring+hill+fl+reviews" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800 font-semibold transition-colors"
          >
            <span>View our Google Reviews</span>
            <Star className="h-4 w-4 text-yellow-400 fill-current" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;